/**
 * TabContextMenu Component
 *
 * Context menu for tab operations:
 * - Rename: Opens inline editing
 * - Duplicate: Creates copy of tab with timeline data
 * - Delete: Removes tab with confirmation if it has tools
 * - Prevents deleting last remaining tab
 */

import { useState } from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { Copy, Edit2, Trash2 } from 'lucide-react';
import { useSceneStore } from '../../stores/sceneStore';
import { useTimelineStore } from '../../stores/timelineStore';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';

interface TabContextMenuProps {
  tabId: string;
  children: React.ReactNode;
}

export function TabContextMenu({ tabId, children }: TabContextMenuProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const tabs = useSceneStore((state) => state.tabs);
  const duplicateTab = useSceneStore((state) => state.duplicateTab);
  const deleteTab = useSceneStore((state) => state.deleteTab);
  const tools = useTimelineStore((state) => state.tools);

  const tab = tabs.find((t) => t.id === tabId);
  const isLastTab = tabs.length === 1;
  const hasTools = tools.length > 0;

  const handleRename = () => {
    // Trigger double-click event on the tab to start editing
    const tabElement = document.querySelector(`[data-tab-id="${tabId}"]`);
    if (tabElement) {
      const event = new MouseEvent('dblclick', {
        bubbles: true,
        cancelable: true,
      });
      tabElement.dispatchEvent(event);
    }
  };

  const handleDuplicate = () => {
    duplicateTab(tabId);
  };

  const handleDelete = () => {
    if (isLastTab) {
      console.warn('Cannot delete the last remaining tab');
      return;
    }

    if (hasTools) {
      setShowDeleteConfirm(true);
    } else {
      deleteTab(tabId);
    }
  };

  const confirmDelete = () => {
    deleteTab(tabId);
    setShowDeleteConfirm(false);
  };

  if (!tab) return <>{children}</>;

  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>

        <ContextMenu.Portal>
          <ContextMenu.Content className="min-w-[180px] rounded-md border border-neutral-700 bg-neutral-800 p-1 shadow-lg">
            {/* Rename */}
            <ContextMenu.Item
              onSelect={handleRename}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-neutral-200 outline-none hover:bg-neutral-700 focus:bg-neutral-700"
            >
              <Edit2 className="h-4 w-4" />
              Rename
            </ContextMenu.Item>

            {/* Duplicate */}
            <ContextMenu.Item
              onSelect={handleDuplicate}
              disabled={tabs.length >= 10}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-neutral-200 outline-none hover:bg-neutral-700 focus:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <Copy className="h-4 w-4" />
              Duplicate
              {tabs.length >= 10 && (
                <span className="ml-auto text-xs text-neutral-500">
                  (Max 10)
                </span>
              )}
            </ContextMenu.Item>

            <ContextMenu.Separator className="my-1 h-px bg-neutral-700" />

            {/* Delete */}
            <ContextMenu.Item
              onSelect={handleDelete}
              disabled={isLastTab}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-red-400 outline-none hover:bg-red-900/20 focus:bg-red-900/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <Trash2 className="h-4 w-4" />
              Delete
              {isLastTab && (
                <span className="ml-auto text-xs text-neutral-500">
                  (Last tab)
                </span>
              )}
            </ContextMenu.Item>
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Tab"
        description={`Are you sure you want to delete "${tab.name}"? This tab contains ${tools.length} tool${tools.length !== 1 ? 's' : ''} and cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmDelete}
        destructive={true}
      />
    </>
  );
}
